import React, { useState, useEffect, useCallback } from "react";
import style from "./style.module.css";
import TruncatedText from "../../Components/TruncatedText";
import { usePermissions } from "../../hooks/usePermissions";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import useSnackbar from "../../context/Snackbar/useSnackbar";
import { parseApiError } from "../../utils/parseApiError";
import { API_BASE } from "../../config/api";
import { formatCurrency } from "../../config/currency";
import Pagination from "../../Components/Pagination";
import DiscountActiveStatus from "../../Components/DiscountActiveStatus";

const PAGE_SIZE = 25;

interface ShopDiscount {
  discount_id: number;
  shop_id: number;
  discount_name: string;
  discount_type: string;
  discount_value: number;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
  is_currently_active: boolean;
  created_at: string;
  updated_at: string;
}

interface EditShopDiscount {
  discount_id?: number;
  shop_id?: number;
  discount_name: string;
  discount_type: string;
  discount_value: string | number;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
}

interface Shop {
  shop_id: number;
  shop_name: string;
}

const ShopDiscountPage = () => {
  const [shopDiscounts, setShopDiscounts] = useState<ShopDiscount[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedDiscounts, setSelectedDiscounts] = useState<number[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editDiscount, setEditDiscount] = useState<EditShopDiscount>({
    discount_name: "",
    discount_type: "percentage",
    discount_value: "",
    valid_from: "",
    valid_until: "",
    is_active: true
  });
  const [customDiscountType, setCustomDiscountType] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Comprehensive list of discount types
  const discountTypes = [
    "percentage",
    "fixed_amount",
    "buy_one_get_one",
    "buy_two_get_one",
    "bulk_discount",
    "seasonal_discount",
    "holiday_discount",
    "student_discount",
    "senior_discount",
    "employee_discount",
    "loyalty_discount",
    "first_time_customer",
    "referral_discount",
    "birthday_discount",
    "anniversary_discount",
    "clearance_discount",
    "flash_sale",
    "early_bird",
    "late_night",
    "weekend_special",
    "midweek_madness",
    "new_customer",
    "returning_customer",
    "volume_discount",
    "quantity_break",
    "combo_discount",
    "bundle_discount",
    "cross_sell",
    "upsell_discount",
    "membership_discount",
    "subscription_discount",
    "prepaid_discount",
    "cash_discount",
    "card_discount",
    "online_discount",
    "in_store_discount",
    "mobile_app_discount",
    "social_media_discount",
    "email_discount",
    "sms_discount",
    "newsletter_discount",
    "review_discount",
    "feedback_discount",
    "survey_discount",
    "custom"
  ];
  const permissions = usePermissions();
  const [cookies] = useCookies();
  const snack = useSnackbar();

  useEffect(() => {
    fetchShops();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const fetchShopDiscounts = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: PAGE_SIZE.toString() });
      if (debouncedSearchTerm) params.set("search", debouncedSearchTerm);
      const response = await authenticatedFetch(`${API_BASE}/shopdiscount?${params.toString()}`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const data = await response.json();
        setShopDiscounts(Array.isArray(data?.data) ? data.data : []);
        setTotalPages(data?.totalPages || 1);
        setTotalItems(data?.total || 0);
      } else {
        console.error("Failed to fetch shop discounts. Status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching shop discounts:", error);
    }
  }, [cookies.auth, page, debouncedSearchTerm]);

  useEffect(() => {
    fetchShopDiscounts();
  }, [fetchShopDiscounts]);

  const fetchShops = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/shop?page=1&limit=100`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const data = await response.json();
        setShops(Array.isArray(data?.data) ? data.data : []);
      } else {
        console.error("Failed to fetch shops. Status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching shops:", error);
    }
  }, [cookies.auth]);

  const handleAdd = () => {
    setEditDiscount({
      discount_name: "",
      discount_type: "percentage",
      discount_value: "",
      valid_from: "",
      valid_until: "",
      is_active: true
    });
    setCustomDiscountType("");
    setShowCustomInput(false);
    setShowAddModal(true);
  };

  const handleDiscountTypeChange = (value: string) => {
    if (value === "custom") {
      setShowCustomInput(true);
      setEditDiscount({...editDiscount, discount_type: customDiscountType || ""});
    } else {
      setShowCustomInput(false);
      setEditDiscount({...editDiscount, discount_type: value});
    }
  };

  const handleCustomDiscountTypeChange = (value: string) => {
    setCustomDiscountType(value);
    setEditDiscount({...editDiscount, discount_type: value});
  };

  const handleUpdate = () => {
    if (selectedDiscounts.length === 0) {
      alert("Please select at least one shop discount to update");
      return;
    }
    
    const discountToEdit = shopDiscounts.find(discount => discount.discount_id === selectedDiscounts[0]);
    if (discountToEdit) {
      // Check if the discount type is in the predefined list
      const isCustomType = !discountTypes.includes(discountToEdit.discount_type);
      
      setEditDiscount({
        discount_id: discountToEdit.discount_id,
        shop_id: discountToEdit.shop_id,
        discount_name: discountToEdit.discount_name,
        discount_type: isCustomType ? "custom" : discountToEdit.discount_type,
        discount_value: discountToEdit.discount_value,
        valid_from: discountToEdit.valid_from.split('T')[0], // Format for date input
        valid_until: discountToEdit.valid_until.split('T')[0],
        is_active: discountToEdit.is_active
      });
      
      // Set custom type state if it's a custom type
      if (isCustomType) {
        setCustomDiscountType(discountToEdit.discount_type);
        setShowCustomInput(true);
      } else {
        setCustomDiscountType("");
        setShowCustomInput(false);
      }
      
      setShowEditModal(true);
    }
  };

  const handleDelete = () => {
    if (selectedDiscounts.length === 0) {
      alert("Please select at least one shop discount to delete");
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setIsLoading(true);
      for (const discountId of selectedDiscounts) {
        const response = await authenticatedFetch(`${API_BASE}/shopdiscount/${discountId}`, {
          method: "DELETE"
        }, cookies.auth);
        
        if (!response.ok) {
          console.error(`Failed to delete shop discount ${discountId}. Status:`, response.status);
        }
      }
      setSelectedDiscounts([]);
      setShowDeleteConfirm(false);
      await fetchShopDiscounts();
    } catch (error) {
      console.error("Error deleting shop discounts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (isEdit: boolean) => {
    try {
      setIsLoading(true);

      // Validate required fields
      if (!editDiscount.discount_name.trim()) {
        snack.onResponse({ message: "Discount name is required", status: 400 });
        return;
      }
      
      if (!editDiscount.shop_id || editDiscount.shop_id === undefined) {
        snack.onResponse({ message: "Please select a shop", status: 400 });
        return;
      }

      if (!editDiscount.valid_from) {
        snack.onResponse({ message: "Valid from date is required", status: 400 });
        return;
      }

      if (!editDiscount.valid_until) {
        snack.onResponse({ message: "Valid until date is required", status: 400 });
        return;
      }

      // Validate dates
      const fromDate = new Date(editDiscount.valid_from);
      const untilDate = new Date(editDiscount.valid_until);
      
      if (fromDate >= untilDate) {
        snack.onResponse({ message: "Valid until date must be after valid from date", status: 400 });
        return;
      }

      const shopId = parseInt(editDiscount.shop_id.toString());
      
      if (isNaN(shopId)) {
        snack.onResponse({ message: "Invalid shop ID", status: 400 });
        return;
      }

      const payload = {
        shop_id: shopId,
        discount_name: editDiscount.discount_name.trim(),
        discount_type: editDiscount.discount_type,
        discount_value: editDiscount.discount_value.toString(),
        valid_from: editDiscount.valid_from,
        valid_until: editDiscount.valid_until,
        is_active: editDiscount.is_active
      };

      const url = isEdit
        ? `${API_BASE}/shopdiscount/${editDiscount.discount_id}`
        : `${API_BASE}/shopdiscount`;
      
      const method = isEdit ? "PUT" : "POST";

      console.log("=== SHOP DISCOUNT SAVE DEBUG ===");
      console.log("URL:", url);
      console.log("Method:", method);
      console.log("Payload:", payload);
      console.log("Auth:", cookies.auth);

      const response = await authenticatedFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      }, cookies.auth);

      console.log("Response status:", response.status);
      console.log("Response ok:", response.ok);

      if (response.ok) {
        const responseData = await response.json();
        console.log("Success response:", responseData);

        setShowEditModal(false);
        setShowAddModal(false);
        setEditDiscount({
          discount_name: "",
          discount_type: "percentage",
          discount_value: "",
          valid_from: "",
          valid_until: "",
          is_active: true
        });
        setSelectedDiscounts([]);
        await fetchShopDiscounts();
        snack.onResponse({
          message: "Shop discount saved successfully!",
          status: 200,
        });
      } else {
        snack.onResponse({ message: await parseApiError(response, "Failed to save shop discount"), status: response.status });
      }
    } catch (error) {
      console.error("Error saving shop discount:", error);
      snack.onResponse({ message: error instanceof Error ? error.message : "Failed to save shop discount", status: 400 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedDiscounts(shopDiscounts.map(discount => discount.discount_id));
    } else {
      setSelectedDiscounts([]);
    }
  };

  const handleSelectDiscount = (discountId: number, checked: boolean) => {
    if (checked) {
      setSelectedDiscounts([discountId]);
    } else {
      setSelectedDiscounts(selectedDiscounts.filter(id => id !== discountId));
    }
  };

  const filteredDiscounts = shopDiscounts;

  const getShopName = (shopId: number | null | undefined) => {
    if (!shopId) return "Unknown";
    const shop = shops.find(s => s.shop_id === shopId);
    return shop ? shop.shop_name : "Unknown";
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "Invalid Date";
    }
  };

  const formatDiscountValue = (value: number, type: string) => {
    switch (type) {
      case "percentage":
        return `${value}%`;
      case "fixed_amount":
        return formatCurrency(value);
      case "buy_one_get_one":
      case "buy_two_get_one":
        return `${value} items`;
      case "bulk_discount":
      case "volume_discount":
      case "quantity_break":
        return `${value} units`;
      default:
        return `${value}`;
    }
  };

  return (
    <div className={style.wrapper}>
      {/* Header Section */}
      <div className={style.header}>
        <h1 className={style.title}>Shop Discount</h1>
        <div className={style.searchSection}>
          <input
            type="text"
            placeholder="Search by discount name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={style.searchInput}
          />
          {permissions.canAdd && (
            <div className={style.actionButtons}>
              <button className={style.actionBtn} onClick={handleAdd}>
                Add
              </button>
              <button className={style.actionBtn} onClick={handleUpdate}>
                Update
              </button>
              <button className={style.actionBtnDanger} onClick={handleDelete}>
                Delete
              </button>
            </div>
          )}
          {permissions.isSales && (
            <div className={style.viewOnlyNotice}>
              <span style={{ color: '#666', fontStyle: 'italic' }}>
                View-only access - Sales Person
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className={style.tableContainer}>
        <table className={style.dataTable}>
          <thead>
            <tr>
              {permissions.canEdit && (
                <th>
                  <input
                    type="checkbox"
                    checked={selectedDiscounts.length === shopDiscounts.length && shopDiscounts.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className={style.checkbox}
                  />
                </th>
              )}
              <th>Discount ID</th>
              <th>Shop</th>
              <th>Discount Name</th>
              <th>Type</th>
              <th>Value</th>
              <th>Valid From</th>
              <th>Valid Until</th>
              <th>Active</th>
              <th>Created At</th>
              <th>Updated At</th>
            </tr>
          </thead>
          <tbody>
            {filteredDiscounts.map((discount) => (
              <tr key={discount.discount_id} className={style.tableRow}>
                {permissions.canEdit && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedDiscounts.includes(discount.discount_id)}
                      onChange={(e) => handleSelectDiscount(discount.discount_id, e.target.checked)}
                      className={style.checkbox}
                    />
                  </td>
                )}
                <td>{discount.discount_id}</td>
                <td>{getShopName(discount.shop_id)}</td>
                <td>{discount.discount_name}</td>
                <td>{discount.discount_type}</td>
                <td>{formatDiscountValue(discount.discount_value, discount.discount_type)}</td>
                <td>{formatDate(discount.valid_from)}</td>
                <td>{formatDate(discount.valid_until)}</td>
                <td><DiscountActiveStatus isCurrentlyActive={discount.is_currently_active} /></td>
                <td>{formatDate(discount.created_at)}</td>
                <td>{formatDate(discount.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} />

      {/* Add Modal */}
      {showAddModal && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Add Shop Discount</h2>
            <div className={style.formGroup}>
              <label>Discount Name:</label>
              <input
                type="text"
                value={editDiscount.discount_name}
                onChange={(e) => setEditDiscount({...editDiscount, discount_name: e.target.value})}
                className={style.input}
              />
            </div>
            <div className={style.formGroup}>
              <label>Shop:</label>
              <select
                value={editDiscount.shop_id || ''}
                onChange={(e) => setEditDiscount({...editDiscount, shop_id: parseInt(e.target.value)})}
                className={style.select}
              >
                <option value="">Select Shop</option>
                {shops.map(shop => (
                  <option key={shop.shop_id} value={shop.shop_id}>
                    {shop.shop_name}
                  </option>
                ))}
              </select>
            </div>
            <div className={style.formGroup}>
              <label>Discount Type:</label>
              <select
                value={editDiscount.discount_type === customDiscountType ? "custom" : editDiscount.discount_type}
                onChange={(e) => handleDiscountTypeChange(e.target.value)}
                className={style.select}
              >
                {discountTypes.map(type => (
                  <option key={type} value={type}>
                    {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
              {showCustomInput && (
                <input
                  type="text"
                  value={customDiscountType}
                  onChange={(e) => handleCustomDiscountTypeChange(e.target.value)}
                  placeholder="Enter custom discount type"
                  className={style.input}
                  style={{ marginTop: '5px' }}
                />
              )}
            </div>
            <div className={style.formGroup}>
              <label>Discount Value:</label>
              <input
                type="text"
                value={editDiscount.discount_value}
                onChange={(e) => setEditDiscount({...editDiscount, discount_value: e.target.value})}
                className={style.input}
                placeholder="Enter any discount value (e.g., 10, 15.5, 25%, $5.00, etc.)"
              />
            </div>
            <div className={style.formGroup}>
              <label>Valid From:</label>
              <input
                type="date"
                value={editDiscount.valid_from}
                onChange={(e) => setEditDiscount({...editDiscount, valid_from: e.target.value})}
                className={style.input}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className={style.formGroup}>
              <label>Valid Until:</label>
              <input
                type="date"
                value={editDiscount.valid_until}
                onChange={(e) => setEditDiscount({...editDiscount, valid_until: e.target.value})}
                className={style.input}
                min={editDiscount.valid_from || new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className={style.formGroup}>
              <label>
                <input
                  type="checkbox"
                  checked={editDiscount.is_active}
                  onChange={(e) => setEditDiscount({...editDiscount, is_active: e.target.checked})}
                />
                {' '}Active (uncheck to pause this discount without changing its dates)
              </label>
            </div>
            <div className={style.modalActions}>
              <button onClick={() => setShowAddModal(false)} className={style.cancelBtn}>
                Cancel
              </button>
              <button onClick={() => handleSave(false)} className={style.saveBtn} disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Edit Shop Discount</h2>
            <div className={style.formGroup}>
              <label>Discount Name:</label>
              <input
                type="text"
                value={editDiscount.discount_name}
                onChange={(e) => setEditDiscount({...editDiscount, discount_name: e.target.value})}
                className={style.input}
              />
            </div>
            <div className={style.formGroup}>
              <label>Shop:</label>
              <select
                value={editDiscount.shop_id || ''}
                onChange={(e) => setEditDiscount({...editDiscount, shop_id: parseInt(e.target.value)})}
                className={style.select}
              >
                <option value="">Select Shop</option>
                {shops.map(shop => (
                  <option key={shop.shop_id} value={shop.shop_id}>
                    {shop.shop_name}
                  </option>
                ))}
              </select>
            </div>
            <div className={style.formGroup}>
              <label>Discount Type:</label>
              <select
                value={editDiscount.discount_type === customDiscountType ? "custom" : editDiscount.discount_type}
                onChange={(e) => handleDiscountTypeChange(e.target.value)}
                className={style.select}
              >
                {discountTypes.map(type => (
                  <option key={type} value={type}>
                    {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
              {showCustomInput && (
                <input
                  type="text"
                  value={customDiscountType}
                  onChange={(e) => handleCustomDiscountTypeChange(e.target.value)}
                  placeholder="Enter custom discount type"
                  className={style.input}
                  style={{ marginTop: '5px' }}
                />
              )}
            </div>
            <div className={style.formGroup}>
              <label>Discount Value:</label>
              <input
                type="text"
                value={editDiscount.discount_value}
                onChange={(e) => setEditDiscount({...editDiscount, discount_value: e.target.value})}
                className={style.input}
                placeholder="Enter any discount value (e.g., 10, 15.5, 25%, $5.00, etc.)"
              />
            </div>
            <div className={style.formGroup}>
              <label>Valid From:</label>
              <input
                type="date"
                value={editDiscount.valid_from}
                onChange={(e) => setEditDiscount({...editDiscount, valid_from: e.target.value})}
                className={style.input}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className={style.formGroup}>
              <label>Valid Until:</label>
              <input
                type="date"
                value={editDiscount.valid_until}
                onChange={(e) => setEditDiscount({...editDiscount, valid_until: e.target.value})}
                className={style.input}
                min={editDiscount.valid_from || new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className={style.formGroup}>
              <label>
                <input
                  type="checkbox"
                  checked={editDiscount.is_active}
                  onChange={(e) => setEditDiscount({...editDiscount, is_active: e.target.checked})}
                />
                {' '}Active (uncheck to pause this discount without changing its dates)
              </label>
            </div>
            <div className={style.modalActions}>
              <button onClick={() => setShowEditModal(false)} className={style.cancelBtn}>
                Cancel
              </button>
              <button onClick={() => handleSave(true)} className={style.saveBtn} disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Confirm Delete</h2>
            <p>Are you sure you want to delete the selected shop discounts?</p>
            <div className={style.modalActions}>
              <button onClick={() => setShowDeleteConfirm(false)} className={style.cancelBtn}>
                Cancel
              </button>
              <button onClick={confirmDelete} className={style.deleteBtn} disabled={isLoading}>
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopDiscountPage;